import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { eventService } from '../../../services/eventService';
import { qrService } from '../../../services/qrService';
import { supabase } from '../../../lib/supabase';
import { Spinner } from '../../../components/ui/Spinner';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { TextArea } from '../../../components/ui/TextArea';
import { QRCodeCanvas } from 'qrcode.react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

export const EventDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState(null);
  const [linkedCards, setLinkedCards] = useState([]);
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modals / State
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [availableCards, setAvailableCards] = useState([]);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id, user]);

  const fetchData = async () => {
    if (!user?.id) return;
    try {
      const ev = await eventService.getEvent(id, user.id);
      setEvent(ev);
      setNotes(ev.notes || '');
      
      const [cards, qrs] = await Promise.all([
        eventService.getLinkedCards(id),
        qrService.getEventQrs(id)
      ]);
      setLinkedCards(cards);
      setQrCodes(qrs);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await eventService.updateEvent(id, user.id, { notes });
    } catch (err) {
      console.error(err);
    } finally {
      setSavingNotes(false);
    }
  };

  const openLinkModal = async () => {
    const { data } = await supabase.from('cards').select('*').eq('user_id', user.id).eq('status', 'published');
    const existingIds = linkedCards.map(lc => lc.card_id);
    setAvailableCards((data || []).filter(c => !existingIds.includes(c.id)));
    setShowLinkModal(true);
  };

  const handleLinkCard = async (cardId) => {
    await eventService.linkCard(id, cardId, linkedCards.length === 0);
    setShowLinkModal(false);
    fetchData();
  };

  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;
  if (!event) return <div className="text-center py-20">Event not found.</div>;

  return (
    <div className="max-w-6xl pb-12">
      <div className="mb-6 flex items-center text-sm font-medium text-slate-500">
        <button onClick={() => navigate('/app/events')} className="hover:text-slate-900 flex items-center">
          <SafeIcon icon={FiIcons.FiArrowLeft} className="mr-1" /> Events
        </button>
        <span className="mx-2">/</span>
        <span className="text-slate-900">{event.name}</span>
      </div>

      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-slate-900">{event.name}</h1>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${
              event.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'
            }`}>
              {event.status}
            </span>
          </div>
          <p className="text-slate-600 flex items-center gap-4 text-sm mt-2">
            <span><SafeIcon icon={FiIcons.FiCalendar} className="inline mr-1"/> {new Date(event.start_date).toLocaleDateString()}</span>
            {event.location && <span><SafeIcon icon={FiIcons.FiMapPin} className="inline mr-1"/> {event.location}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate(`/app/events/${id}/analytics`)}>
            <SafeIcon icon={FiIcons.FiBarChart2} className="mr-2" /> Analytics
          </Button>
          <Button variant="ghost" onClick={() => navigate(`/app/events/${id}/connections`)}>
            <SafeIcon icon={FiIcons.FiUsers} className="mr-2" /> Connections
          </Button>
          <Button variant="secondary" onClick={() => navigate(`/app/events/${id}/edit`)}>
            <SafeIcon icon={FiIcons.FiEdit2} className="mr-2" /> Edit
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {/* Linked Cards Section */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
              <h2 className="text-lg font-bold text-slate-900">Linked Cards</h2>
              <Button size="sm" variant="secondary" onClick={openLinkModal}>
                <SafeIcon icon={FiIcons.FiLink} className="mr-2" /> Link Card
              </Button>
            </div>
            {linkedCards.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <SafeIcon icon={FiIcons.FiCreditCard} className="text-3xl mx-auto mb-2 opacity-50" />
                <p>No cards linked yet. Link a card to enable event QR codes.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {linkedCards.map(lc => (
                  <div key={lc.card_id} className="flex justify-between items-center bg-slate-50 p-4 rounded-xl border border-slate-100">
                    <div>
                      <h4 className="font-bold text-slate-900">{lc.cards.internal_name}</h4>
                      <p className="text-xs text-slate-500">meetoi.com/c/{lc.cards.public_slug}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => eventService.unlinkCard(id, lc.card_id).then(fetchData)}>
                      Unlink
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* QR Codes Section */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
              <h2 className="text-lg font-bold text-slate-900">Event QR Codes</h2>
              <Button size="sm" variant="secondary" onClick={() => setShowQrModal(true)} disabled={linkedCards.length === 0}>
                <SafeIcon icon={FiIcons.FiPlus} className="mr-2" /> New QR Code
              </Button>
            </div>
            {qrCodes.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <SafeIcon icon={FiIcons.FiMaximize} className="text-3xl mx-auto mb-2 opacity-50" />
                <p>Create an event-specific QR code to properly attribute networking activity.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {qrCodes.map(qr => (
                  <div key={qr.id} className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex flex-col items-center text-center">
                    <div className="bg-white p-2 rounded-xl mb-3 shadow-sm border border-slate-100">
                      <QRCodeCanvas value={`${window.location.origin}${window.location.pathname}#/q/${qr.token}`} size={120} />
                    </div>
                    <h4 className="font-bold text-slate-900 text-sm mb-1">{qr.label || 'Event QR'}</h4>
                    <p className="text-xs text-slate-500 mb-3">Card: {qr.cards?.internal_name}</p>
                    <div className="flex gap-2 w-full">
                      <Button variant="ghost" size="sm" className="flex-1" onClick={() => {
                        const canvas = document.querySelector('canvas');
                        const url = canvas.toDataURL('image/png');
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `event-qr-${qr.label || 'card'}.png`;
                        link.click();
                      }}>Download</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Private Notes Section */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 h-fit">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-bold text-slate-900">Private Notes</h2>
            {savingNotes && <span className="text-xs text-slate-400 animate-pulse">Saving...</span>}
          </div>
          <TextArea 
            value={notes}
            onChange={e => setNotes(e.target.value)}
            onBlur={handleSaveNotes}
            placeholder="Record observations, goals, or follow-up strategies for this event. These notes remain completely private."
            rows={10}
            className="bg-yellow-50/30 resize-none text-sm"
          />
        </div>
      </div>

      {/* Modals */}
      {showLinkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Link a Card</h3>
            {availableCards.length === 0 ? (
              <p className="text-slate-500 mb-6">No eligible published cards available to link.</p>
            ) : (
              <div className="space-y-2 mb-6">
                {availableCards.map(c => (
                  <button key={c.id} onClick={() => handleLinkCard(c.id)} className="w-full text-left p-3 rounded-lg border border-slate-200 hover:border-slate-900 hover:bg-slate-50">
                    <p className="font-bold">{c.internal_name}</p>
                    <p className="text-xs text-slate-500">{c.full_name} • {c.job_title}</p>
                  </button>
                ))}
              </div>
            )}
            <Button variant="ghost" onClick={() => setShowLinkModal(false)} className="w-full">Cancel</Button>
          </div>
        </div>
      )}

      {showQrModal && (
        <CreateQrModal 
          linkedCards={linkedCards} 
          eventId={id} 
          userId={user.id} 
          onClose={() => setShowQrModal(false)}
          onSuccess={fetchData}
        />
      )}
    </div>
  );
};

const CreateQrModal = ({ linkedCards, eventId, userId, onClose, onSuccess }) => {
  const [selectedCard, setSelectedCard] = useState(linkedCards[0]?.card_id || '');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await qrService.createEventQr(eventId, selectedCard, label, userId);
      onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md">
        <h3 className="text-xl font-bold mb-4">Generate Event QR Code</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Select Card</label>
            <select 
              className="w-full rounded-lg border border-slate-300 px-3 py-2"
              value={selectedCard}
              onChange={e => setSelectedCard(e.target.value)}
            >
              {linkedCards.map(lc => (
                <option key={lc.card_id} value={lc.card_id}>{lc.cards.internal_name}</option>
              ))}
            </select>
          </div>
          <Input 
            label="QR Label (Optional)"
            placeholder="e.g. Main Stage, Booth 4A"
            value={label}
            onChange={e => setLabel(e.target.value)}
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" isLoading={saving}>Generate</Button>
          </div>
        </form>
      </div>
    </div>
  );
};